import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedStatus = localStorage.getItem('subscription_status');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      if (savedStatus) setSubscriptionStatus(savedStatus);
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('subscription_status');
      delete api.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { token, user: userData, subscription_status } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('subscription_status', subscription_status || 'pending');
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    setSubscriptionStatus(subscription_status || 'pending');
    return { user: userData, subscription_status };
  };

  const register = async (email, password, restaurantName) => {
    const response = await api.post('/auth/register', { 
      email, 
      password, 
      restaurant_name: restaurantName 
    });
    const { token, user: userData } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('subscription_status', 'pending');
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    setSubscriptionStatus('pending');
    return userData;
  };

  const updateSubscriptionStatus = (status) => {
    setSubscriptionStatus(status);
    localStorage.setItem('subscription_status', status);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('subscription_status');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setSubscriptionStatus(null);
  };

  const value = {
    user,
    subscriptionStatus,
    loading,
    login,
    register,
    logout,
    fetchUser,
    updateSubscriptionStatus
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
